import {Controller, useFieldArray, useForm, useWatch} from 'react-hook-form'
import {ComboboxProvider, PopoverProvider, PopoverDisclosure, SelectProvider} from '@ariakit/react'
import {
    StyledField,
    StyledFooter,
    StyledPopover,
    StyledPopoverContent,
    StyledSelectedOperator,
    StyledWhere
} from '@components/commons/table-filters/style.ts'
import {TKey, useTranslation} from '@/translations/i18n.tsx'
import {ChevronDownIcon, FilterFunnel02Icon, PlusIcon, Trash01Icon} from '@components/ui/icon/Icon.tsx'
import {Divider} from '@components/ui/divider/Divider.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectList,
    SelectPopover,
    SelectTrigger
} from '@components/ui/select-atoms/SelectAtoms.tsx'
import {ObjectKeys, workSectorToLabel, workServiceToLabel} from '@utilities/helpers.ts'
import {FC, useState} from 'react'
import {zodResolver} from '@hookform/resolvers/zod'
import {InputDate} from '@/components/commons/input-date/InputDate'
import dayjs from '@/dayjs.ts'
import {MultiSelectCheckbox} from '@components/commons/multi-select-checkbox/MultiSelectCheckbox.tsx'
import {UserBase} from '@/features/users/types.ts'
import {userStatusToLabel} from '@/features/users/utils.ts'
import {RoleFilterSelect} from '@/features/users/components/role-filter-select/RoleFilterSelect.tsx'
import {SpecializationFilterSelect} from '@/features/users/components/specialization-filter-select/SpecializationFilterSelect.tsx'
import {WorkAreaFilterSelect} from '@/features/users/components/work-area-filter-select/WorkAreaFilterSelect.tsx'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'
import {PatientAgeRange, TableFilterOperator, TableFiltersValidationSchema, WorkSector} from '@/types.ts'
import {InputSelect} from '@components/commons/input-select/InputSelect.tsx'
import {AslFilterSelect} from '@/features/users/components/asl-filter-select/AslFilterSelect'
import {CityFilterSelect} from '@/features/users/components/city-filter-select/CityFilterSelect'
import {ProvinceFilterSelect} from '@/features/users/components/province-filter-select/ProvinceFilterSelect'
import {
    authorizationToLabel,
    rejectedReasonToLabel,
    treatmentRequestStatusToLabel,
    typeToLabel
} from '@/features/treatment-requests/utils'
import {
    TreatmentRequestAuthorization,
    TreatmentRequestRejectedReason,
    TreatmentRequestStatus,
    TreatmentRequestType
} from '@/features/treatment-requests/types'
import {InputText} from '../input-text/InputText'
import {TherapistDoctorFilter} from '@/features/users/components/therapist-doctor-filter-select/TherapistDoctorFilter'
import {DiagnosisFilter} from '@/features/treatment-requests/components/diagnosis-filter/DiagnosisFilter'
import {
    projectAssistiveRegimeToLabel,
    projectProgressStatusToLabel,
    projectSectorToLabel,
    projectStatusToLabel,
    projectTypeToLabel
} from '@/features/projects/utils'
import {ProjectProgressStatus, ProjectStatus, ProjectType} from '@/features/projects/types'
import {NeuromuscularDiagnosisFilter} from '@/features/projects/components/neuromuscular-diagnosis-filter/NeuromuscularDiagnosisFilter'
import {TableButton} from '../table-button/TableButton'
import {isValidDate} from '@/components/commons/input-date/utils'
import {accessGroupNameEnumToLabel, agendaEventStatusMapToLabel, eventTypeMapToLabel} from '@/features/agenda/utils'
import {accessesStatusToLabel, accessTypeToLabel} from '@/features/accesses/utils'
import {AccessRateCode, RemappedSiarAccessType} from '@/features/accesses/types'
import {SiarBulkExportType, SiarExportStatus} from '@/features/projects/siar-bulk-export/types'
import {siarExportStatusToLabel, siarExportTypeToLabel} from '@/features/projects/siar-bulk-export/utils'
import {UserFilterMultiSelect} from '@/features/users/components/user-filter-multi-select/UserFilterMultiSelect'
import {PatientFilterMultiSelect} from '@/features/patients/components/patient-filter-multi-select/PatientFilterMultiSelect'
import {PatientStatus} from '@/features/patients/types'
import {patientStatusToLabel} from '@/features/patients/utils'
import {InputTime} from '../input-time/InputTime'
import {AgendaEventStatus} from '@/features/agenda/types'

const operatorToLabel = {
    and: 'table_filters:and',
    or: 'table_filters:or'
} as const satisfies Record<TableFiltersValidationSchema['operator'], TKey>

const filterOperatorToTKey = {
    '<': 'table_filters:operator_less',
    '<=': 'table_filters:operator_less_equal',
    '=': 'table_filters:operator_equal',
    '!=': 'table_filters:operator_not_equal',
    '>=': 'table_filters:operator_greater_equal',
    '>': 'table_filters:operator_greater',
    in: 'table_filters:operator_equal',
    'not in': 'table_filters:operator_not_equal',
    empty: 'table_filters:operator_empty',
    'not empty': 'table_filters:operator_not_empty',
    like: 'table_filters:operator_like'
} as const satisfies Record<Exclude<TableFilterOperator, 'is' | 'is not'>, TKey>

const filterToOperators = {
    status: TableFilterOperator.extract(['in', 'not in']).options,
    projectStatus: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    sector: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    authorization: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    birthDate: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    createdAt: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>']).options,
    lastLoginAt: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    roleId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    serviceType: TableFilterOperator.extract(['in', 'not in']).options,
    specializationId: TableFilterOperator.extract(['in', 'not in']).options,
    outRegion: TableFilterOperator.extract(['=']).options,
    residenceAslDistrictId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    residenceCityId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    residenceProvinceId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    authorizationDate: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    type: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    score: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    rejectedAt: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    rejectedReason: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    therapistId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    referringDoctorId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    diagnosisId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    acceptedAt: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    startDate: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    expirationDate: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    endDate: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    code: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    durationDays: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    absenceDays: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    suspensionDays: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    progressStatus: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    completedAccessesPerCent: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty'])
        .options,
    openingExportedAt: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    openingTransmissionAt: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    closingExportedAt: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    date: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    closingTransmissionAt: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    neuromuscularDiagnosisId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    mobilePhone: TableFilterOperator.extract(['like']).options,
    telephone: TableFilterOperator.extract(['like']).options,
    email: TableFilterOperator.extract(['like']).options,
    taxCode: TableFilterOperator.extract(['like']).options,
    communicatedOnTime: TableFilterOperator.extract(['=', '!=', 'empty', 'not empty']).options,
    groupName: TableFilterOperator.extract(['=', '!=']).options,
    accessType: TableFilterOperator.extract(['in', 'not in']).options,
    projectCode: TableFilterOperator.extract(['=']).options,
    projectStartDate: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    projectExpirationDate: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>', 'empty', 'not empty']).options,
    remappedSiarAccessType: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    aslId: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    report: TableFilterOperator.extract(['empty', 'not empty']).options,
    rateCode: TableFilterOperator.extract(['in', 'not in', 'empty', 'not empty']).options,
    access_100: TableFilterOperator.extract(['empty', 'not empty']).options,
    absence_20: TableFilterOperator.extract(['empty', 'not empty']).options,
    absence_50: TableFilterOperator.extract(['empty', 'not empty']).options,
    absence_nr: TableFilterOperator.extract(['empty', 'not empty']).options,
    consultantId: TableFilterOperator.extract(['in', 'not in']).options,
    patientId: TableFilterOperator.extract(['in', 'not in']).options,
    isRemote: TableFilterOperator.extract(['=', '!=']).options,
    patientAgeRange: TableFilterOperator.extract(['in', 'not in']).options,
    assistiveRegime: TableFilterOperator.extract(['=', '!=', 'empty', 'not empty']).options,
    isSuspended: TableFilterOperator.extract(['=', '!=']).options,
    consultant: TableFilterOperator.extract(['in', 'not in']).options,
    consultantFullName: TableFilterOperator.extract(['in', 'not in']).options,
    time: TableFilterOperator.extract(['=', '!=']).options,
    contractType: TableFilterOperator.extract(['=']).options,
    specializationGroup: TableFilterOperator.extract(['=', '!=', 'in', 'not in']).options,
    contactEmail: TableFilterOperator.extract(['like']).options,
    isRenewed: TableFilterOperator.extract(['=', '!=']).options,
    age: TableFilterOperator.extract(['>=', '<=']).options,
    exportType: TableFilterOperator.extract(['in', 'not in']).options,
    dateFrom: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>']).options,
    dateTo: TableFilterOperator.extract(['<', '<=', '=', '!=', '>=', '>']).options,
    isExternalStructure: TableFilterOperator.extract(['=', '!=']).options,
    workAreas: TableFilterOperator.extract(['in', 'not in']).options
} as const satisfies Record<
    TableFiltersValidationSchema['fields'][number]['key'],
    Array<Exclude<TableFilterOperator, 'is' | 'is not'>>
>

interface TableFiltersProps {
    availableFilters: Array<{key: keyof typeof filterToOperators; label: TKey}>
    filtersParams?: string
    entity:
        | 'users'
        | 'patients'
        | 'treatmentRequests'
        | 'projects'
        | 'accesses'
        | 'patientEvents'
        | 'userEvents'
        | 'siarExports'
    onApply: (filters: TableFiltersValidationSchema) => void
    /* Hides the AND/OR selector and forces the applied operator to `and`. Used by lists whose
    payload injects a fixed scope field: `or` would let the user filters bypass that scope. */
    isOperatorLocked?: boolean
}
export const TableFilters: FC<TableFiltersProps> = ({
    availableFilters,
    entity,
    filtersParams,
    onApply,
    isOperatorLocked = false
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const filtersCount = filtersParams ? ObjectKeys(JSON.parse(filtersParams).fields).length : 0

    return (
        <PopoverProvider open={isOpen} setOpen={setIsOpen} placement="bottom-end">
            <PopoverDisclosure render={<TableButton isActive={filtersCount >= 1} />}>
                <FilterFunnel02Icon />
                {t('table_filters:button_text', {count: filtersCount})}
                <ChevronDownIcon />
            </PopoverDisclosure>
            <StyledPopover unmountOnHide gutter={8}>
                <Content
                    availableFilters={availableFilters}
                    filtersParams={filtersParams}
                    onApply={onApply}
                    entity={entity}
                    setIsOpen={setIsOpen}
                    isOperatorLocked={isOperatorLocked}
                />
            </StyledPopover>
        </PopoverProvider>
    )
}

// Searchable single select for the filter field key
const FilterKeySelect = ({
    availableFilters,
    value,
    onChange
}: {
    availableFilters: Array<{key: keyof typeof filterToOperators; label: TKey}>
    value: string
    onChange: (value: string) => void
}) => {
    const {t} = useTranslation()
    const [comboboxValue, setComboboxValue] = useState('')
    const searchFilters = availableFilters.filter(filter =>
        t(filter.label).toLowerCase().includes(comboboxValue.toLowerCase())
    )

    return (
        <ComboboxProvider value={comboboxValue} setValue={setComboboxValue} resetValueOnHide>
            <SelectProvider placement="bottom" defaultValue="" value={value} setValue={onChange}>
                <SelectTrigger
                    value={value}
                    placeholder={t('table_filters:select_field')}
                    displayValue={value => {
                        const filter = availableFilters.find(filter => filter.key == value)
                        if (!filter) {
                            return value
                        }
                        return t(filter.label)
                    }}
                />
                <SelectComboboxPopover sameWidth={false} style={{width: '240px'}}>
                    <SelectComboboxInput placeholder={t('commons:search_placeholder')} />
                    {searchFilters.length == 0 ? (
                        <InputHelpText helpText={t('commons:no_results')} />
                    ) : (
                        <SelectComboboxList>
                            {searchFilters.map(filter => (
                                <SelectItem hasItemCheckbox value={filter.key} key={filter.key}>
                                    {t(filter.label)}
                                </SelectItem>
                            ))}
                        </SelectComboboxList>
                    )}
                </SelectComboboxPopover>
            </SelectProvider>
        </ComboboxProvider>
    )
}

const Content = ({
    availableFilters,
    filtersParams,
    onApply,
    entity,
    setIsOpen,
    isOperatorLocked
}: {
    availableFilters: Array<{key: keyof typeof filterToOperators; label: TKey}>
    filtersParams: string | undefined
    onApply: (filters: TableFiltersValidationSchema) => void
    entity:
        | 'users'
        | 'patients'
        | 'treatmentRequests'
        | 'projects'
        | 'accesses'
        | 'patientEvents'
        | 'userEvents'
        | 'siarExports'
    setIsOpen: (isOpen: boolean) => void
    isOperatorLocked: boolean
}) => {
    const {t} = useTranslation()
    const parsedFiltersParams = filtersParams
        ? TableFiltersValidationSchema.parse(JSON.parse(filtersParams))
        : undefined
    const form = useForm<TableFiltersValidationSchema>({
        defaultValues:
            parsedFiltersParams && parsedFiltersParams.fields.length >= 1
                ? parsedFiltersParams
                : {
                      fields: [{key: undefined, operator: undefined, value: undefined}],
                      operator: 'and'
                  },
        resolver: zodResolver(TableFiltersValidationSchema)
    })
    const fieldArray = useFieldArray({control: form.control, name: 'fields'})
    const operatorWatch = useWatch({name: 'operator', control: form.control})
    const isFormDirty = ObjectKeys(form.formState.dirtyFields).length >= 1

    const onSubmit = form.handleSubmit(formValues => {
        // The URL may still carry `or` (e.g. hand-edited): coerce it when the operator is locked
        const appliedValues = isOperatorLocked ? {...formValues, operator: 'and' as const} : formValues
        onApply(appliedValues)
        form.reset(appliedValues)
        setIsOpen(false)
    })

    return (
        <>
            <StyledPopoverContent>
                <div>
                    {fieldArray.fields.map((fieldArrayItem, index) => (
                        <StyledField key={fieldArrayItem.id}>
                            <div style={{paddingRight: '6px'}}>
                                {index == 0 ? (
                                    <StyledWhere>{t('table_filters:where')}</StyledWhere>
                                ) : index == 1 && isOperatorLocked ? (
                                    <StyledSelectedOperator>{t(operatorToLabel.and)}</StyledSelectedOperator>
                                ) : index == 1 ? (
                                    <Controller
                                        control={form.control}
                                        name="operator"
                                        render={({field}) => (
                                            <SelectProvider
                                                placement="bottom-start"
                                                value={field.value}
                                                setValue={field.onChange}
                                            >
                                                <SelectTrigger
                                                    value={field.value}
                                                    displayValue={value =>
                                                        t(
                                                            operatorToLabel[
                                                                value as TableFiltersValidationSchema['operator']
                                                            ]
                                                        )
                                                    }
                                                />
                                                <SelectPopover sameWidth={false}>
                                                    <SelectList>
                                                        <SelectItem hasItemCheckbox value="and">
                                                            {t(operatorToLabel.and)}
                                                        </SelectItem>
                                                        <SelectItem hasItemCheckbox value="or">
                                                            {t(operatorToLabel.or)}
                                                        </SelectItem>
                                                    </SelectList>
                                                </SelectPopover>
                                            </SelectProvider>
                                        )}
                                    />
                                ) : (
                                    <StyledSelectedOperator>
                                        {t(operatorToLabel[isOperatorLocked ? 'and' : operatorWatch])}
                                    </StyledSelectedOperator>
                                )}
                            </div>
                            <Controller
                                control={form.control}
                                name={`fields.${index}.key`}
                                render={({field}) => (
                                    <FilterKeySelect
                                        availableFilters={availableFilters}
                                        value={field.value}
                                        onChange={value => {
                                            field.onChange(value)
                                            /* @ts-expect-error [TS2322] "We need to add empty fields even if the schema need them" */
                                            fieldArray.update(index, {key: value, operator: '', value: ''})
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                control={form.control}
                                name={`fields.${index}.operator`}
                                render={({field}) => (
                                    <SelectProvider
                                        defaultValue=""
                                        placement="bottom"
                                        value={field.value}
                                        setValue={value => {
                                            field.onChange(value)
                                            if (value == 'empty' || value == 'not empty') {
                                                fieldArray.update(index, {
                                                    key: fieldArrayItem.key,
                                                    operator: value,
                                                    value: null
                                                })
                                            }
                                        }}
                                    >
                                        <SelectTrigger
                                            disabled={!fieldArrayItem.key}
                                            value={field.value}
                                            placeholder={t('table_filters:select_operator')}
                                            displayValue={value =>
                                                t(
                                                    filterOperatorToTKey[
                                                        value as TableFiltersValidationSchema['fields'][number]['operator']
                                                    ]
                                                )
                                            }
                                        />
                                        <SelectPopover sameWidth={false}>
                                            <SelectList>
                                                {filterToOperators[fieldArrayItem.key]?.map(operator => (
                                                    <SelectItem hasItemCheckbox value={operator} key={operator}>
                                                        {t(filterOperatorToTKey[operator])}
                                                    </SelectItem>
                                                ))}
                                            </SelectList>
                                        </SelectPopover>
                                    </SelectProvider>
                                )}
                            />
                            <Controller
                                name={`fields.${index}.value`}
                                control={form.control}
                                render={({field}) => {
                                    //the watch is needed because fieldArrayItem.operator is not updated when the operator is changed
                                    const operator = form.watch(`fields.${index}.operator`)
                                    if (operator == 'empty' || operator == 'not empty') {
                                        return <div />
                                    }
                                    switch (fieldArrayItem.key) {
                                        case 'birthDate':
                                        case 'createdAt':
                                        case 'lastLoginAt':
                                        case 'authorizationDate':
                                        case 'rejectedAt':
                                        case 'acceptedAt':
                                        case 'startDate':
                                        case 'expirationDate':
                                        case 'endDate':
                                        case 'openingExportedAt':
                                        case 'openingTransmissionAt':
                                        case 'closingExportedAt':
                                        case 'closingTransmissionAt':
                                        case 'date':
                                        case 'projectStartDate':
                                        case 'projectExpirationDate':
                                        case 'dateFrom':
                                        case 'dateTo':
                                            return (
                                                <InputDate
                                                    placeholder="GG/MM/AAAA"
                                                    value={
                                                        field.value && isValidDate(field.value as string, 'YYYY-MM-DD')
                                                            ? dayjs(field.value as string).toDate()
                                                            : null
                                                    }
                                                    onChange={value =>
                                                        field.onChange(
                                                            value &&
                                                                isValidDate(
                                                                    dayjs(value).format('YYYY-MM-DD'),
                                                                    'YYYY-MM-DD'
                                                                )
                                                                ? dayjs(value).format('YYYY-MM-DD')
                                                                : null
                                                        )
                                                    }
                                                />
                                            )
                                        case 'time':
                                            return (
                                                <InputTime
                                                    startTime="00:00"
                                                    endTime="23:59"
                                                    placeholder={t('table_filters:select_value')}
                                                    value={field.value as string}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'roleId':
                                            return (
                                                <RoleFilterSelect
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'specializationId':
                                            return (
                                                <SpecializationFilterSelect
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'workAreas':
                                            return (
                                                <WorkAreaFilterSelect
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'sector':
                                            if (entity == 'patientEvents' || entity == 'userEvents') {
                                                // events.sector only accepts outpatient | domiciliary
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={WorkSector.extract([
                                                            'outpatient',
                                                            'domiciliary'
                                                        ]).options.map(workSector => ({
                                                            value: workSector,
                                                            label: t(workSectorToLabel[workSector])
                                                        }))}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            if (entity == 'projects') {
                                                // projects.sector accepts outpatient | domiciliary | mixed
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={(['outpatient', 'domiciliary', 'mixed'] as const).map(
                                                            sector => ({
                                                                value: sector,
                                                                label: t(projectSectorToLabel[sector])
                                                            })
                                                        )}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={WorkSector.options.map(workSector => ({
                                                        value: workSector,
                                                        label: t(workSectorToLabel[workSector])
                                                    }))}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'authorization':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={TreatmentRequestAuthorization.options.map(
                                                        authorization => ({
                                                            value: authorization,
                                                            label: t(authorizationToLabel[authorization])
                                                        })
                                                    )}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'type':
                                            if (entity == 'projects') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={ProjectType.options.map(type => ({
                                                            value: type,
                                                            label: t(projectTypeToLabel[type])
                                                        }))}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            if (entity == 'accesses') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={Object.entries(accessTypeToLabel).map(
                                                            ([key, value]) => ({
                                                                value: key,
                                                                label: t(value)
                                                            })
                                                        )}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={TreatmentRequestType.options.map(type => ({
                                                        value: type,
                                                        label: t(typeToLabel[type])
                                                    }))}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'status':
                                            if (entity == 'patientEvents' || entity == 'userEvents') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={AgendaEventStatus.options.map(status => ({
                                                            value: status,
                                                            label: t(agendaEventStatusMapToLabel[status])
                                                        }))}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            if (entity == 'users') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={UserBase.shape.status1.options.map(status => ({
                                                            value: status,
                                                            label: t(userStatusToLabel[status])
                                                        }))}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            if (entity == 'patients') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={PatientStatus.options.map(status => ({
                                                            value: status,
                                                            label: t(patientStatusToLabel[status])
                                                        }))}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            if (entity == 'treatmentRequests') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={TreatmentRequestStatus.options.map(status => ({
                                                            value: status,
                                                            label: t(treatmentRequestStatusToLabel[status], {count: 2})
                                                        }))}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            if (entity == 'projects') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={ProjectStatus.options.map(status => ({
                                                            value: status,
                                                            label: t(projectStatusToLabel[status])
                                                        }))}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            if (entity == 'accesses') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={Object.entries(accessesStatusToLabel).map(
                                                            ([key, value]) => ({
                                                                value: key,
                                                                label: t(value)
                                                            })
                                                        )}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            if (entity == 'siarExports') {
                                                return (
                                                    <MultiSelectCheckbox
                                                        value={(field.value as string[]) || []}
                                                        onChange={field.onChange}
                                                        options={SiarExportStatus.options.map(status => ({
                                                            value: status,
                                                            label: t(siarExportStatusToLabel[status])
                                                        }))}
                                                        placeholder={t('table_filters:select_value')}
                                                    />
                                                )
                                            }
                                            return <div />
                                        case 'projectStatus':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={ProjectStatus.options.map(status => ({
                                                        value: status,
                                                        label: t(projectStatusToLabel[status])
                                                    }))}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'serviceType':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={PatientAgeRange.options.map(patientAgeRange => ({
                                                        value: patientAgeRange,
                                                        label: t(workServiceToLabel[patientAgeRange])
                                                    }))}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'rejectedReason':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={TreatmentRequestRejectedReason.options.map(
                                                        rejectedReason => ({
                                                            value: rejectedReason,
                                                            label: t(rejectedReasonToLabel[rejectedReason])
                                                        })
                                                    )}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'outRegion':
                                            return (
                                                <InputSelect
                                                    sameWidth={false}
                                                    onChange={field.onChange}
                                                    value={field.value as string}
                                                    optionsMap={{yes: 'commons:yes', no: 'commons:no'}}
                                                />
                                            )
                                        case 'residenceAslDistrictId':
                                        case 'aslId':
                                            return (
                                                <AslFilterSelect
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'residenceCityId':
                                            return (
                                                <CityFilterSelect
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'residenceProvinceId':
                                            return (
                                                <ProvinceFilterSelect
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'age':
                                        case 'score':
                                        case 'code':
                                        case 'durationDays':
                                        case 'absenceDays':
                                        case 'suspensionDays':
                                        case 'mobilePhone':
                                        case 'telephone':
                                        case 'projectCode':
                                            return (
                                                <InputText
                                                    type="number"
                                                    value={field.value as number}
                                                    onChange={event => {
                                                        const value = event.target.value
                                                        if (value == '') {
                                                            return field.onChange(value)
                                                        }
                                                        if (isNaN(Number(value))) {
                                                            return
                                                        }
                                                        field.onChange(Number(value))
                                                    }}
                                                />
                                            )
                                        case 'therapistId':
                                            return (
                                                <TherapistDoctorFilter
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    group="therapist"
                                                />
                                            )
                                        case 'referringDoctorId':
                                            return (
                                                <TherapistDoctorFilter
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    group="referring_doctor"
                                                />
                                            )
                                        case 'diagnosisId':
                                            return (
                                                <DiagnosisFilter
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'neuromuscularDiagnosisId':
                                            return (
                                                <NeuromuscularDiagnosisFilter
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'progressStatus':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={ProjectProgressStatus.options.map(status => ({
                                                        value: status,
                                                        label: t(projectProgressStatusToLabel[status])
                                                    }))}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'completedAccessesPerCent':
                                            return (
                                                <InputSelect
                                                    sameWidth={false}
                                                    value={field.value?.toString()}
                                                    onChange={value => field.onChange(Number(value))}
                                                    placeholder={t('table_filters:select_value')}
                                                    optionsEntries={[
                                                        ['25', '25%'],
                                                        ['50', '50%'],
                                                        ['75', '75%'],
                                                        ['100', '100%']
                                                    ]}
                                                />
                                            )
                                        case 'contactEmail':
                                        case 'email':
                                        case 'taxCode':
                                            return (
                                                <InputText
                                                    type="text"
                                                    value={field.value as string}
                                                    onChange={event => field.onChange(event.target.value)}
                                                />
                                            )
                                        case 'communicatedOnTime':
                                            return (
                                                <InputSelect
                                                    sameWidth={false}
                                                    value={field.value?.toString()}
                                                    onChange={value => field.onChange(value === 'true')}
                                                    placeholder={t('table_filters:select_value')}
                                                    optionsEntries={[
                                                        ['true', t('commons:over_48_hours')],
                                                        ['false', t('commons:within_48_hours')]
                                                    ]}
                                                />
                                            )
                                        case 'groupName':
                                            return (
                                                <InputSelect
                                                    sameWidth={false}
                                                    value={field.value?.toString()}
                                                    onChange={field.onChange}
                                                    placeholder={t('table_filters:select_value')}
                                                    optionsMap={accessGroupNameEnumToLabel}
                                                />
                                            )
                                        case 'accessType':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    placeholder={t('table_filters:select_value')}
                                                    options={Object.entries(eventTypeMapToLabel).map(
                                                        ([key, value]) => ({
                                                            value: key,
                                                            label: t(value)
                                                        })
                                                    )}
                                                />
                                            )
                                        case 'remappedSiarAccessType':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={RemappedSiarAccessType.options.map(type => ({
                                                        value: type,
                                                        label: type
                                                    }))}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'rateCode':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={AccessRateCode.options.map(rateCode => ({
                                                        value: rateCode,
                                                        label: rateCode
                                                    }))}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        case 'consultantId':
                                        case 'consultant':
                                        case 'consultantFullName':
                                            return (
                                                <UserFilterMultiSelect
                                                    value={field.value as string[]}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'patientId':
                                            return (
                                                <PatientFilterMultiSelect
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                />
                                            )
                                        case 'isRemote':
                                        case 'isRenewed':
                                        case 'isSuspended':
                                            return (
                                                <InputSelect
                                                    sameWidth={false}
                                                    value={field.value?.toString()}
                                                    onChange={field.onChange}
                                                    optionsEntries={[
                                                        ['true', t('commons:yes')],
                                                        ['false', t('commons:no')]
                                                    ]}
                                                />
                                            )
                                        case 'assistiveRegime':
                                            return (
                                                <InputSelect
                                                    sameWidth={false}
                                                    value={field.value?.toString()}
                                                    onChange={field.onChange}
                                                    optionsMap={projectAssistiveRegimeToLabel}
                                                />
                                            )
                                        case 'exportType':
                                            return (
                                                <MultiSelectCheckbox
                                                    value={(field.value as string[]) || []}
                                                    onChange={field.onChange}
                                                    options={SiarBulkExportType.options.map(type => ({
                                                        value: type,
                                                        label: t(siarExportTypeToLabel[type])
                                                    }))}
                                                    placeholder={t('table_filters:select_value')}
                                                />
                                            )
                                        default:
                                            return <div />
                                    }
                                }}
                            />
                            <Button
                                variant="ghost"
                                shape="square"
                                onClick={() => fieldArray.remove(index)}
                                style={{justifySelf: 'flex-end'}}
                            >
                                <Trash01Icon />
                            </Button>
                            {form.formState.errors.fields?.[index] && <InputHelpText error={t('errors:required')} />}
                        </StyledField>
                    ))}
                </div>
                <Button
                    variant="ghost"
                    onClick={() =>
                        /* @ts-expect-error [TS2322] "We need to add empty fields even if the schema need them" */
                        fieldArray.append({key: '', operator: '', value: ''})
                    }
                >
                    <PlusIcon />
                    {t('table_filters:add_rule')}
                </Button>
            </StyledPopoverContent>
            <Divider direction="horizontal" />
            <StyledFooter>
                <Button
                    variant="ghost"
                    onClick={() => {
                        fieldArray.replace([])
                        onSubmit()
                    }}
                    disabled={fieldArray.fields.length == 0}
                >
                    <Trash01Icon />
                    {t('table_filters:remove_filters')}
                </Button>
                <Flexbox gap={2}>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            form.reset()
                        }}
                        disabled={!isFormDirty}
                    >
                        {t('commons:restore')}
                    </Button>
                    <Button variant="primaryNeutral" disabled={!isFormDirty} onClick={onSubmit}>
                        {t('commons:apply')}
                    </Button>
                </Flexbox>
            </StyledFooter>
        </>
    )
}
